import * as THREE from 'three';
import type { Vec3 } from '../../sim/mathx';
import type { RadarConfig } from '../../sim/radarConfig';
import type { InterpolatedTelemetry } from '../../state/interpolate';
import type { TelemetrySnapshot } from '../../state/telemetryBuffer';
import { CoverageVolume } from './CoverageVolume';
import { computePpiScene, type PpiScene } from './ppi';
import { SelectedTrackHalo } from './SelectedTrackHalo';
import { SweepIndicator } from './SweepIndicator';
import { TrackMarkers } from './TrackMarkers';

export type RadarVizConfig = Pick<
  RadarConfig,
  'mountEnu' | 'mountYaw' | 'azSector' | 'elLimits' | 'rangeMin' | 'rangeMax' | 'scanPeriod'
>;

export type RadarVizLayer = 'coverage' | 'sweep' | 'tracks' | 'halo' | 'ppi';

/** Facade owning all radar visualization layers and their persistent resources. */
export class RadarViz {
  readonly coverage: CoverageVolume;
  readonly sweep: SweepIndicator;
  readonly tracks: TrackMarkers;
  readonly halo: SelectedTrackHalo;
  ppiVisible = true;

  private readonly config: RadarVizConfig;
  private latestPpiScene: PpiScene;
  private disposed = false;

  constructor(scene: THREE.Scene, config: RadarVizConfig) {
    this.config = {
      ...config,
      mountEnu: { ...config.mountEnu },
      azSector: { ...config.azSector },
      elLimits: { ...config.elLimits },
    };
    this.coverage = new CoverageVolume(this.config);
    this.sweep = new SweepIndicator(this.config);
    this.tracks = new TrackMarkers(this.config.mountEnu);
    this.halo = new SelectedTrackHalo();
    scene.add(this.coverage, this.sweep, this.tracks, this.halo);
    this.latestPpiScene = computePpiScene(
      this.config,
      [],
      this.config.mountEnu,
      this.sweep.currentSweepAzimuth,
    );
  }

  update(
    snapshot: TelemetrySnapshot,
    interp: InterpolatedTelemetry,
    scoutEnu: Vec3,
    dtSeconds: number,
  ): void {
    if (this.disposed) return;
    this.sweep.update(dtSeconds);
    this.tracks.update(snapshot.tracks, interp.tracks, dtSeconds);
    this.halo.update(snapshot.companion.selectedTrackId, interp.tracks, scoutEnu);
    this.latestPpiScene = computePpiScene(
      this.config,
      interp.tracks,
      scoutEnu,
      this.sweep.currentSweepAzimuth,
    );
  }

  setLayerVisible(layer: RadarVizLayer, on: boolean): void {
    switch (layer) {
      case 'coverage':
        this.coverage.visible = on;
        break;
      case 'sweep':
        this.sweep.visible = on;
        break;
      case 'tracks':
        this.tracks.visible = on;
        break;
      case 'halo':
        this.halo.visible = on;
        break;
      case 'ppi':
        this.ppiVisible = on;
        break;
    }
  }

  ppiScene(): PpiScene {
    return this.latestPpiScene;
  }

  dispose(): void {
    if (this.disposed) return;
    this.disposed = true;
    this.coverage.dispose();
    this.sweep.dispose();
    this.tracks.dispose();
    this.halo.dispose();
  }
}
